import { Icon, IconName, IconProps } from "@zapier/zinnia-icons";

import {
  BetaDataTableCell as Cell,
  IconButton,
  Link,
  ServiceIcon,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";

import { AssetTypes, assetTypeToSentenceCase } from "@zapier/utils";
import { HighlightedText } from "../HighlightedText";
import { TooltipText } from "../TooltipText";
import {
  AppTableInteractionEventOption,
  useEmitAppTableInteractionEvent,
  useEmitAssetRowInteractionEvent,
  useEmitZapKebabInteractionEvent,
  ZapKebabInteractionOption,
} from "../../../../events";
import tableStyles from "../Table.module.css";
import styles from "./TitleCell.module.css";

interface TitleProps {
  color?: IconProps["color"];
  icon?: IconName; // optional icon to display next to title
  serviceIconSrc?: string; // optional service icon to display next to title
  title?: string | null;
  subtitle?: string | null;
  url?: string;
  isUntitled?: boolean;
  searchQuery?: string | null;
  id: string | number;
  allowAccordion?: boolean; // whether to make space for a possible accordion element
  hasAccordion?: boolean; // whether the accordion element should be rendereed
  accordionLevel?: number; // depth of accordion element
  isAccordionExpanded?: boolean; // is accordion open or closed?
  onExpandAccordion?: (isFolderExpanded: boolean) => void; // callback for toggling accordion
  hiddenSelection?: boolean;
  assetType?: AssetTypes;
  folderZapTableRow?: boolean;
  description?: string | null;
  className?: string;
  style?: React.CSSProperties;
  titleAttribute?: string; // HTML attribute for hovering over asset icon
  onClick?: () => void; // optional click handler (used for locked items that open a modal)
  badge?: React.ReactNode; // optional badge to display inline with the title
  // Where the badge sits: "end" right-aligns it against the cell edge (the
  // long-standing behaviour, so it stays the default), "inline" places it
  // directly after the title text.
  badgePlacement?: "end" | "inline";
  subtitleIcon?: IconName; // optional icon to display next to subtitle
  subtitleIconTooltip?: string; // tooltip text for subtitle icon
  // Translated label for the asset-type icon tooltip. Overrides the
  // mechanically-derived `assetTypeToSentenceCase(assetType)` fallback, which
  // cannot express casing that differs from the enum value (e.g. "Next Gen
  // Zap" vs. the derived "Next gen zap").
  assetTypeTooltip?: string;
  [key: `data-${string}`]: string | undefined;
}

const Title = ({
  id,
  title,
  subtitle,
  url,
  serviceIconSrc,
  icon,
  color,
  isUntitled,
  searchQuery,
  allowAccordion,
  hasAccordion,
  accordionLevel = 0,
  isAccordionExpanded,
  onExpandAccordion,
  hiddenSelection,
  assetType,
  folderZapTableRow,
  description,
  className,
  style,
  titleAttribute,
  onClick,
  badge,
  badgePlacement = "end",
  subtitleIcon,
  subtitleIconTooltip,
  assetTypeTooltip,
  ...restProps
}: TitleProps) => {
  const isBadgeInline = badgePlacement === "inline";
  const { emit: emitZapKebabInteractionEvent } =
    useEmitZapKebabInteractionEvent();
  const { emit: emitAppTableInteractionEvent } =
    useEmitAppTableInteractionEvent();
  const { emit: emitAssetRowInteractionEvent } =
    useEmitAssetRowInteractionEvent();
  // A title with nothing to activate (a trashed asset, for one) must not be
  // announced or focusable as a link.
  const isInteractive = !!url || !!onClick;
  const titleId = `${assetType || "Zap"}-TitleCell-${title}`;
  const assetTypeLabel =
    assetTypeTooltip ?? (assetType ? assetTypeToSentenceCase(assetType) : "");
  const titleBody = (
    <span
      className={styles.title}
      title={titleAttribute || ""}
      id={`${assetType || "Zap"}TitleCell-span-${title}`}
      data-testid="AssetItem-title"
    >
      {!!serviceIconSrc && (
        <span className={styles.serviceIcon}>
          <ServiceIcon
            hasBorder={false}
            serviceName={title || ""}
            size="medium"
            src={serviceIconSrc}
          />
        </span>
      )}

      <span className={styles.subtitleWrapper}>
        <TooltipText
          id={`title-${id}`}
          label={title as string}
          placement="bottom"
        >
          <Text type="Body2" color={isUntitled ? "TextWeakest" : "TextDefault"}>
            <HighlightedText text={title} highlight={searchQuery} />
          </Text>
        </TooltipText>
        <span className={styles.subtitleRow}>
          {subtitleIcon && (
            <span className={styles.subtitleIcon}>
              <Tooltip content={subtitleIconTooltip || ""} placement="bottom">
                {/* Only a naming role may carry a label, and `tag` is the
                    trigger's supported way to set one. */}
                <TooltipFocusableTrigger
                  aria-label={subtitleIconTooltip}
                  tag="button"
                >
                  <Icon
                    aria-hidden={true}
                    name={subtitleIcon}
                    color="GrayWarm6"
                    size={16}
                  />
                </TooltipFocusableTrigger>
              </Tooltip>
            </span>
          )}
          <TooltipText
            id={`subtitle-${id}`}
            label={subtitle as string}
            placement="bottom"
          >
            <Text type="MinimalPrint1Multiline" color="TextWeaker">
              {subtitle}
            </Text>
          </TooltipText>
        </span>
      </span>
    </span>
  );
  return (
    <Cell
      style={{ paddingLeft: accordionLevel * 24, ...style }}
      className={
        hiddenSelection
          ? `${styles.hiddenSelection} ${className} ${tableStyles.titleCell}`
          : `${tableStyles.titleCell} ${className} ${folderZapTableRow ? styles.folderZapTableRowTitle : ""}`
      }
      {...restProps}
    >
      <span
        className={`${styles.root} ${allowAccordion && styles.allowAccordion}`}
      >
        {allowAccordion && (
          <span>
            {hasAccordion && (
              <span
                className={`${styles.caretIcon} ${isAccordionExpanded ? styles.caretOpen : styles.caretClosed}`}
                data-testid={`${id}-arrow-caretIcon`}
              >
                <IconButton
                  aria-expanded={isAccordionExpanded ? true : false}
                  aria-label={
                    isAccordionExpanded
                      ? `Hide subfolders for ${title}`
                      : `Show subfolders for ${title}`
                  }
                  name="arrowSmallDown"
                  onPress={() => onExpandAccordion?.(!isAccordionExpanded)}
                  size="small"
                  variant="ghost"
                />
              </span>
            )}
          </span>
        )}
        <span className={styles.titleLinkContainer}>
          {!!icon && (
            <span
              aria-hidden={assetTypeLabel ? undefined : true}
              aria-label={assetTypeLabel || undefined}
              className={styles.assetIcon}
              role={assetTypeLabel ? "img" : undefined}
              title={assetTypeLabel || undefined}
            >
              <Icon
                aria-hidden={true}
                name={icon}
                color={color || "BrandOrange"}
                className={styles.icon}
                size={18}
              />
            </span>
          )}

          {isInteractive ? (
            <Link
              href={url}
              className={`${styles.titleLinkDefault} ${!!url && styles.titleLink} ${!!onClick && !url && styles.clickable} ${isBadgeInline ? styles.titleLinkInline : ""}`}
              id={titleId}
              onPress={() => {
                if (onClick && !url) {
                  onClick();
                  return;
                }
                if (assetType === AssetTypes.App) {
                  emitAppTableInteractionEvent(
                    AppTableInteractionEventOption.ViewApp,
                  );
                } else if (assetType === AssetTypes.Zap || !assetType) {
                  emitZapKebabInteractionEvent(ZapKebabInteractionOption.View);
                } else {
                  emitAssetRowInteractionEvent(assetType, id);
                }
              }}
            >
              {titleBody}
            </Link>
          ) : (
            /* The grid gives arrow-key focus to a cell's first focusable
               descendant, so the title stays focusable (but never tabbable) to
               keep it, rather than a trailing icon, the Name column's target. */
            <span
              className={`${styles.titleLinkDefault} ${isBadgeInline ? styles.titleLinkInline : ""}`}
              id={titleId}
              tabIndex={-1}
            >
              {titleBody}
            </span>
          )}
          {badge !== undefined && badge !== false && (
            <span
              className={`${styles.badge} ${isBadgeInline ? styles.badgeInline : ""}`}
            >
              {badge}
            </span>
          )}
        </span>
        {description && (
          <span className={styles.description}>
            <Text type="MinimalPrint1Multiline" color="TextWeaker">
              {description}
            </Text>
          </span>
        )}
      </span>
    </Cell>
  );
};

export default Title;
